import React, { useState, useEffect, useCallback, useRef } from 'react';
import TransformationsPanel from './TransformationsPanel';
import { apiFetch } from '../api';

const CONFIDENCE_STYLES = {
  high: 'bg-green-100 text-green-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-red-100 text-red-800',
};

const CUSTOM_TYPES = ['Single-select', 'Multi-select', 'Text', 'Longtext', 'Number', 'Date'];

function MappingRow({ mapping, frontifyFields, usedTargets, onChange, suggestLoading }) {
  const {
    source_field, frontify_field, confidence, note,
    is_custom, custom_type, custom_name, skip, display_name,
    source_field_count = 1,
  } = mapping;

  // Determine row state
  const isResolved = skip || frontify_field || (is_custom && custom_type);
  const borderColor = skip ? 'border-slate-200' :
    isResolved ? 'border-green-200' : 'border-amber-300';
  const bgColor = skip ? 'bg-slate-50' : 'bg-white';

  // Available targets = all template fields not already used by another row
  const availableTargets = frontifyFields.filter(f =>
    !usedTargets.has(f.name) || f.name === frontify_field
  );
  const standardTargets = availableTargets.filter(f => !f.is_custom);
  const customTemplateTargets = availableTargets.filter(f => f.is_custom);

  const handleTargetChange = (e) => {
    const val = e.target.value;
    if (val === '__skip__') {
      onChange({ ...mapping, frontify_field: null, skip: true, is_custom: false, custom_type: null });
    } else if (val === '__custom__') {
      onChange({ ...mapping, frontify_field: null, skip: false, is_custom: true, custom_type: custom_type || 'Text', auto_excluded: false });
    } else if (val === '') {
      onChange({ ...mapping, frontify_field: null, skip: false, is_custom: false, auto_excluded: false });
    } else {
      onChange({ ...mapping, frontify_field: val, skip: false, is_custom: false, custom_type: null, auto_excluded: false });
    }
  };

  return (
    <div className={`border ${borderColor} ${bgColor} rounded-lg p-4 mb-3`}>
      <div className="grid gap-4 items-start lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,1.3fr)_minmax(0,1fr)]">
        {/* Source field */}
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-800 [overflow-wrap:anywhere]">
            {display_name || source_field}
          </div>
          {display_name && display_name !== source_field && (
            <div className="mt-1 text-[11px] font-mono text-slate-400 [overflow-wrap:anywhere]">
              {source_field}
            </div>
          )}
          <div className="mt-2 flex flex-wrap gap-1.5">
            {source_field_count > 1 && (
              <span className="inline-block text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-700">
                {source_field_count} source columns combined
              </span>
            )}
            {confidence && (
              <span className={`inline-block text-xs px-2 py-0.5 rounded ${CONFIDENCE_STYLES[confidence] || ''}`}>
                {confidence} confidence
              </span>
            )}
          </div>
        </div>

        {/* Target selector */}
        <div className="min-w-0">
          <select
            value={skip ? '__skip__' : is_custom ? '__custom__' : (frontify_field || '')}
            onChange={handleTargetChange}
            className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">-- Select target --</option>
            <optgroup label="Standard fields">
              {standardTargets.map(f => (
                <option key={f.name} value={f.name}>
                  {f.name}{f.mandatory ? ' (required)' : ''}
                </option>
              ))}
            </optgroup>
            {customTemplateTargets.length > 0 && (
              <optgroup label="Custom fields (existing)">
                {customTemplateTargets.map(f => (
                  <option key={f.name} value={f.name}>
                    {f.name}
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label="Other options">
              <option value="__custom__">Create new custom field</option>
              <option value="__skip__">Skip this field</option>
            </optgroup>
          </select>

          {/* Custom field options */}
          {is_custom && !skip && (
            <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_10rem]">
              <input
                type="text"
                placeholder="Field name in Frontify"
                value={custom_name || source_field}
                onChange={(e) => onChange({ ...mapping, custom_name: e.target.value })}
                className="min-w-0 border border-slate-300 rounded px-2 py-1 text-sm"
              />
              <select
                value={custom_type || 'Text'}
                onChange={(e) => onChange({ ...mapping, custom_type: e.target.value })}
                className="border border-slate-300 rounded px-2 py-1 text-sm"
              >
                {CUSTOM_TYPES.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Note */}
        <div className="min-w-0">
          {suggestLoading && !note ? (
            <div className="text-xs text-slate-300 pt-1 italic">Getting suggestion…</div>
          ) : (
            <div className="text-xs text-slate-500 pt-1">{note}</div>
          )}
          {frontify_field === 'Location' && !skip && (
            <div className="text-xs text-blue-600 mt-1">
              ℹ Filename and Directory will be automatically extracted from this field at apply time.
            </div>
          )}
          {skip && <div className="text-xs text-slate-400 mt-1 italic">Will not be included in the Frontify file</div>}
        </div>
      </div>
    </div>
  );
}


export default function FieldMapping() {
  const [mappings, setMappings] = useState([]);
  const [frontifyFields, setFrontifyFields] = useState([]);
  const [targetSchema, setTargetSchema] = useState(null);
  const [schemaUploading, setSchemaUploading] = useState(false);
  const [schemaNotice, setSchemaNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState(null);
  const [s3Root, setS3Root] = useState('frontify-transfer');
  // True when transformations have been detected in a previous session for this
  // source file. Allows the panel to render on tab return even when `applied`
  // has reset to null (React state does not persist across mount/unmount).
  const [transformationsDetected, setTransformationsDetected] = useState(false);

  // AI suggestion state — separate from initial load
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState(null);
  const [suggestMode, setSuggestMode] = useState(null);
  const [aiStatus, setAiStatus] = useState({ configured: false, mode: 'rule_based' });
  const [mappingSummary, setMappingSummary] = useState(null);
  const [showExcluded, setShowExcluded] = useState(false);

  // Polling ref for background suggest thread (same pattern as RationalizationWorkbench)
  const suggestPollRef = useRef(null);

  // Mount: load source schema + Frontify schema + any saved mapping.
  // No suggestion call on mount — rows render immediately and are fully
  // interactive. The user triggers AI suggestions manually via the button.
  useEffect(() => {
    Promise.all([
      apiFetch(`/schema`).then(r =>
        r.json().then(d => { if (!r.ok) throw new Error(d.error || `Server error ${r.status}`); return d; })
      ),
      apiFetch(`/frontify-schema`).then(r => r.json()),
      apiFetch(`/mapping/load`).then(r => r.json()).catch(() => ({ exists: false })),
      apiFetch(`/project/settings`).then(r => r.json()).catch(() => ({ s3_root: 'frontify-transfer' })),
      // Check if transformations have already been detected in a previous visit.
      // If detected_at is non-null, the panel is shown even after navigating away
      // and back (when `applied` has reset to null from React's remount).
      apiFetch(`/transformations/summary`).then(r => r.json()).catch(() => ({ detected_at: null })),
      apiFetch(`/ai/status`).then(r => r.json()).catch(() => ({ configured: false, mode: 'rule_based' })),
    ]).then(([schemaResult, fsResult, loadResult, settingsResult, transformSummary, currentAiStatus]) => {
      setS3Root(settingsResult.s3_root || 'frontify-transfer');
      setAiStatus(currentAiStatus);
      setTargetSchema(fsResult);
      if (transformSummary.detected_at) {
        setTransformationsDetected(true);
      }
      setFrontifyFields(fsResult.fields || []);

      // The raw schema remains available for audit. Field mapping uses the
      // migration-focused logical view: repeated arrays are consolidated and
      // deterministic system/native metadata is pre-excluded but reversible.
      const mappingView = schemaResult.mapping_view || {};
      const columns = mappingView.fields || schemaResult.columns || {};
      setMappingSummary(mappingView.summary || null);
      let rows = Object.entries(columns).map(([col, field]) => {
        const autoExcluded = field.default_disposition === 'skip';
        return {
          source_field: col,
          source_fields: field.source_fields || [col],
          source_field_count: field.source_field_count || 1,
          raw_column_count: field.raw_column_count || 1,
          group_type: field.group_type || null,
          aggregation: field.aggregation || 'first_nonempty',
          classification: field.classification || null,
          default_disposition: field.default_disposition || 'review',
          display_name: field.display_name || col,
          preferred_custom_type: field.preferred_custom_type || null,
          auto_excluded: autoExcluded,
          frontify_field: null,
          is_custom: false,
          custom_type: null,
          custom_name: null,
          skip: autoExcluded,
          confidence: autoExcluded ? (field.default_confidence || 'medium') : null,
          note: autoExcluded ? (field.default_reason || 'Excluded by local migration rules') : null,
        };
      });

      // Overlay saved selections if a confirmed mapping exists from a prior session.
      // Selections pre-fill silently; saved=true is NOT inferred — user must re-confirm.
      if (loadResult.exists && loadResult.mapping && Array.isArray(loadResult.mapping.mappings)) {
        const savedByField = {};
        for (const m of loadResult.mapping.mappings) {
          if (m.source_field) savedByField[m.source_field] = m;
        }
        rows = rows.map(row => {
          const sv = savedByField[row.source_field];
          if (!sv) return row;
          return {
            ...row,
            frontify_field: sv.frontify_field ?? row.frontify_field,
            is_custom: sv.is_custom ?? row.is_custom,
            custom_type: sv.custom_type ?? row.custom_type,
            custom_name: sv.custom_name ?? row.custom_name,
            skip: sv.skip ?? row.skip,
            // Preserve any AI notes from the saved mapping so they appear on load
            confidence: sv.confidence ?? row.confidence,
            note: sv.note ?? row.note,
            auto_excluded: sv.auto_excluded ?? row.auto_excluded,
          };
        });
      }

      setMappings(rows);
      setLoading(false);
    }).catch(err => {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('no source file') || msg.toLowerCase().includes('upload') || msg.toLowerCase().includes('schema not yet')) {
        setError('Your session was interrupted — the server no longer has your source file. Please use "New Project" to re-upload your file and start again.');
      } else {
        setError(msg || 'Failed to load field data. Please refresh and try again.');
      }
      setLoading(false);
    });
  }, []);

  const handleChange = useCallback((index, updated) => {
    setMappings(prev => {
      const next = [...prev];
      next[index] = updated;
      return next;
    });
    setSaved(false);
    setApplied(null);
  }, []);

  const handleSaveSettings = useCallback(async (newS3Root) => {
    const value = (newS3Root ?? s3Root).trim() || 'frontify-transfer';
    try {
      await apiFetch(`/project/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ s3_root: value }),
      });
    } catch {
      // Settings save failure is non-blocking — silently ignore
    }
  }, [s3Root]);

  // Stop the suggest polling interval (safe to call when already stopped).
  const stopSuggestPolling = useCallback(() => {
    if (suggestPollRef.current) {
      clearInterval(suggestPollRef.current);
      suggestPollRef.current = null;
    }
  }, []);

  // Apply a suggestions array to the current mapping rows.
  // Overlays confidence/note on all rows; only overwrites target selection
  // if the row has no current selection.
  const applySuggestions = useCallback((suggestions) => {
    const suggByField = {};
    for (const s of suggestions) {
      if (s.source_field) suggByField[s.source_field] = s;
    }
    setMappings(prev => prev.map(row => {
      const sugg = suggByField[row.source_field];
      if (!sugg) return row;
      const hasSelection = row.frontify_field !== null || row.is_custom || row.skip;
      return {
        ...row,
        source_fields: sugg.source_fields || row.source_fields,
        source_field_count: sugg.source_field_count ?? row.source_field_count,
        raw_column_count: sugg.raw_column_count ?? row.raw_column_count,
        group_type: sugg.group_type ?? row.group_type,
        aggregation: sugg.aggregation ?? row.aggregation,
        classification: sugg.classification ?? row.classification,
        default_disposition: sugg.default_disposition ?? row.default_disposition,
        display_name: sugg.display_name || row.display_name,
        preferred_custom_type: sugg.preferred_custom_type || row.preferred_custom_type,
        auto_excluded: sugg.auto_excluded ?? row.auto_excluded,
        confidence: sugg.confidence,
        note: sugg.note,
        ...(hasSelection ? {} : {
          frontify_field: sugg.frontify_field,
          is_custom: sugg.is_custom,
          custom_type: sugg.custom_type,
          custom_name: sugg.custom_name,
          skip: sugg.skip,
        }),
      };
    }));
    setSuggestLoading(false);
  }, []);

  // Cleanup polling on unmount
  useEffect(() => () => stopSuggestPolling(), [stopSuggestPolling]);

  // Fetch AI suggestions on demand.
  // When an API key is configured, the backend starts a background thread and
  // returns immediately — we poll /mapping/suggest/progress every 2 seconds.
  // When no API key is configured, the backend runs rule-based fallback
  // synchronously and returns suggestions directly.
  const handleGetSuggestions = async () => {
    setSuggestLoading(true);
    setSuggestError(null);
    stopSuggestPolling();
    try {
      const res = await apiFetch(`/mapping/suggest`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Server error ${res.status}`);

      // Immediate result — rule-based fallback (no API key), apply directly
      if (Array.isArray(data.suggestions)) {
        setSuggestMode(data.mode || 'rule_based');
        if (data.mapping_summary) setMappingSummary(data.mapping_summary);
        applySuggestions(data.suggestions);
        return;
      }

      // Background thread started — poll for completion
      if (data.status === 'started' || data.status === 'already_running') {
        suggestPollRef.current = setInterval(async () => {
          try {
            const pr = await apiFetch(`/mapping/suggest/progress`);
            const prog = await pr.json();
            if (prog.status === 'complete') {
              stopSuggestPolling();
              setSuggestMode(prog.mode || 'openai');
              if (prog.mapping_summary) setMappingSummary(prog.mapping_summary);
              // Surface fallback note (e.g. "AI response could not be parsed — using rule-based suggestions")
              if (prog.note) {
                setSuggestError(`Note: ${prog.note}`);
              }
              applySuggestions(prog.suggestions || []);
            } else if (prog.status === 'error') {
              stopSuggestPolling();
              setSuggestError(prog.message || 'AI suggestions failed. You can still map fields manually.');
              setSuggestLoading(false);
            }
            // 'starting' / 'running' — keep polling
          } catch {
            // Network hiccup during polling — keep trying
          }
        }, 2000);
        return;
      }

      throw new Error('Unexpected response from suggestion service');
    } catch (err) {
      stopSuggestPolling();
      const msg = err.message || '';
      if (msg.toLowerCase().includes('no source file') || msg.toLowerCase().includes('upload')) {
        setSuggestError('Source file is no longer available. Please use "New Project" to re-upload and start again.');
      } else {
        setSuggestError(msg || 'Could not load AI suggestions. You can still map fields manually.');
      }
      setSuggestLoading(false);
    }
  };

  // Compute which Frontify targets are already used
  const usedTargets = new Set(
    mappings.filter(m => m.frontify_field && !m.skip).map(m => m.frontify_field)
  );

  // Check if all fields are resolved
  const unresolvedCount = mappings.filter(m =>
    !m.skip && !m.frontify_field && !(m.is_custom && m.custom_type)
  ).length;
  const visibleMappingRows = mappings
    .map((mapping, index) => ({ mapping, index }))
    .filter(({ mapping }) => !(mapping.auto_excluded && mapping.skip));
  const excludedMappingRows = mappings
    .map((mapping, index) => ({ mapping, index }))
    .filter(({ mapping }) => mapping.auto_excluded && mapping.skip);

  // Read required fields from the selected Frontify template. ID and the S3
  // Location are generated by the tool, so they do not need source mappings.
  const mandatoryMappingFields = (targetSchema?.mandatory_fields || ['ID', 'Filename', 'Location', 'Title'])
    .filter(field => !['ID', 'Location'].includes(field));
  const mappedTargets = new Set(
    mappings.filter(m => !m.skip && m.frontify_field).map(m => m.frontify_field)
  );
  const hasLocationMapping = mappedTargets.has('Location');
  const missingMandatory = mandatoryMappingFields.filter(field => {
    if (field === 'Title') return !mappedTargets.has('Title');
    // Filename and Directory are derivable when Location is mapped
    return !mappedTargets.has(field) && !hasLocationMapping;
  });
  const missingPathField = missingMandatory.includes('Filename') || missingMandatory.includes('Directory');
  const mandatoryError = missingMandatory.length > 0
    ? `Cannot confirm mapping: the following required Frontify fields are not mapped: ${missingMandatory.join(', ')}.${missingPathField ? ' Tip: if your source file contains a full asset path (e.g. assetPath), map it to Location — Filename and Directory will be extracted automatically.' : ''}`
    : null;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        mappings: mappings,
        synthetic_id: true,
      };
      const res = await apiFetch(`/mapping/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      setSaved(true);
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleApply = async () => {
    setError(null);
    try {
      const res = await apiFetch(`/mapping/apply`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Apply failed');
      setApplied(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTargetSchemaUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const confirmed = window.confirm(
      'Use this target field template for the project? Any saved mapping and downstream work will need to be reviewed again.'
    );
    if (!confirmed) {
      event.target.value = '';
      return;
    }
    setSchemaUploading(true);
    setError(null);
    setSchemaNotice(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiFetch(`/frontify-schema/upload`, { method: 'POST', body: formData });
      const result = await response.json();
      if (!response.ok || result.error) throw new Error(result.message || result.error || 'Upload failed');
      const schemaResponse = await apiFetch(`/frontify-schema`);
      const schema = await schemaResponse.json();
      setFrontifyFields(schema.fields || []);
      setTargetSchema(schema);
      setSaved(false);
      if (Array.isArray(result.warnings) && result.warnings.length > 0) {
        setSchemaNotice(result.warnings);
      }
    } catch (err) {
      setError(err.message || 'Could not save the target Frontify schema.');
    }
    setSchemaUploading(false);
    event.target.value = '';
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-lg font-medium text-slate-700">Loading field data…</div>
        <div className="text-sm text-slate-500 mt-2">Reading source schema and any saved mapping</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Field Mapping</h2>
          <p className="text-sm text-slate-500 mt-1">
            Map each source field to a Frontify target. Synthetic IDs will be generated automatically.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-center xl:justify-end">
          {/* Suggestion button — distinguishes paid AI from local rules. */}
          <div className="flex flex-col items-end gap-1">
            {suggestLoading ? (
              <span className="flex items-center gap-1.5 text-sm text-purple-600 font-medium">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                {aiStatus.configured ? 'Analyzing fields with AI…' : 'Applying local rules…'}
              </span>
            ) : (
              <button
                onClick={handleGetSuggestions}
                className="px-3 py-1.5 bg-purple-50 text-purple-700 text-sm font-medium rounded-lg border border-purple-200 hover:bg-purple-100 transition"
              >
                {aiStatus.configured ? '✦ Analyze with AI' : 'Analyze with local rules'}
              </button>
            )}
            <span className={`text-[11px] ${aiStatus.configured ? 'text-purple-600' : 'text-slate-500'}`}>
              {suggestMode === 'openai'
                ? 'AI suggestions applied'
                : suggestMode === 'rule_based'
                  ? 'Local rules applied — no API call'
                  : aiStatus.configured
                    ? 'Uses AI when clicked'
                    : 'Uses local rules — no API call'}
            </span>
          </div>
          {unresolvedCount > 0 && (
            <span className="text-sm text-amber-600 font-medium">
              {unresolvedCount} field{unresolvedCount > 1 ? 's' : ''} need{unresolvedCount === 1 ? 's' : ''} review
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={unresolvedCount > 0 || !!mandatoryError || saving}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition"
          >
            {saving ? 'Saving...' : saved ? 'Saved ✓' : 'Confirm Mapping'}
          </button>
          {saved && !applied && (
            <button
              onClick={handleApply}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition"
            >
              Apply to Data
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-5 flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-slate-800">Target Frontify field set</div>
          <div className="text-xs text-slate-500 mt-1">
            {targetSchema?.schema_source === 'project'
              ? `${targetSchema.fields?.length || 0} fields · project template locked to this export`
              : `${targetSchema?.fields?.length || 0} standard fields · bundled default template`}
          </div>
        </div>
        <label className="px-3 py-1.5 bg-slate-100 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-200 cursor-pointer transition flex-shrink-0">
          {schemaUploading
            ? 'Checking template…'
            : targetSchema?.schema_source === 'project'
              ? 'Replace Frontify template'
              : 'Upload Frontify template'}
          <input
            type="file"
            accept=".csv,.xlsx"
            onChange={handleTargetSchemaUpload}
            disabled={schemaUploading}
            className="hidden"
          />
        </label>
      </div>

      {mappingSummary && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-800">
                {mappingSummary.raw_field_count?.toLocaleString()} source columns reduced to{' '}
                {mappingSummary.review_field_count?.toLocaleString()} mapping decisions
              </div>
              <p className="text-xs text-slate-500 mt-1 max-w-3xl">
                Repeated structures are combined, and empty, system, sparse, or native technical metadata is excluded locally before AI analysis. Nothing is removed from the source audit.
              </p>
              {mappingSummary.consolidated_group_count > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  {mappingSummary.consolidated_raw_field_count.toLocaleString()} source columns form{' '}
                  {mappingSummary.consolidated_group_count.toLocaleString()} consolidated field groups.
                </p>
              )}
            </div>
            {excludedMappingRows.length > 0 && (
              <button
                type="button"
                onClick={() => setShowExcluded(value => !value)}
                className="self-start px-3 py-1.5 bg-white text-slate-700 text-xs font-medium rounded-lg border border-slate-300 hover:bg-slate-100 transition sm:self-center"
                aria-expanded={showExcluded}
              >
                {showExcluded ? 'Hide excluded fields' : `Review ${mappingSummary.auto_skipped_raw_field_count.toLocaleString()} excluded source columns`}
              </button>
            )}
          </div>
        </div>
      )}

      {schemaNotice && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-sm text-amber-800">
          <div className="font-medium mb-1">Template notes</div>
          <ul className="list-disc pl-5 space-y-1">
            {schemaNotice.map(note => <li key={note}>{note}</li>)}
          </ul>
        </div>
      )}

      {mandatoryError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-700">
          {mandatoryError}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {suggestError && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 text-sm text-amber-800">
          {suggestError}
        </div>
      )}

      {applied && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 text-sm text-green-800">
          Mapping applied successfully. {applied.row_count.toLocaleString()} rows mapped
          to {applied.mapped_columns.length} Frontify columns.
          {applied.skipped_raw_field_count > 0 && (
            <span> {applied.skipped_raw_field_count.toLocaleString()} source columns were excluded.</span>
          )}
        </div>
      )}

      {(applied || transformationsDetected) && <TransformationsPanel />}

      {/* Synthetic ID notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 text-sm text-blue-800">
        <strong>ID</strong> — A unique synthetic ID will be generated for each asset automatically.
        This keeps the identifier stable even if filenames change during migration.
      </div>

      {/* S3 destination + Location preview */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-semibold text-slate-700 whitespace-nowrap">Location</span>
          <span className="text-slate-500 text-xs whitespace-nowrap">S3 destination folder:</span>
          <input
            type="text"
            value={s3Root}
            onChange={(e) => setS3Root(e.target.value)}
            onBlur={(e) => handleSaveSettings(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.target.blur(); } }}
            placeholder="frontify-transfer"
            className="border border-slate-300 rounded px-2 py-1 text-sm w-52 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Location will be auto-generated as:{' '}
          <span className="font-mono text-slate-700 bg-slate-100 px-1 rounded">
            {(s3Root.trim() || 'frontify-transfer').replace(/\/+$/, '')}/&#123;Directory&#125;/&#123;Filename&#125;
          </span>
          {' '}— this is the intended upload path, not a check against a live S3 bucket.
        </p>
        <p className="text-xs text-slate-400 mt-1">
          A hypothetical folder is fine while planning. Before the production import, make this match the actual Frontify transfer folder.
        </p>
      </div>

      {/* Column headers */}
      <div className="hidden lg:grid gap-4 mb-2 px-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,1.3fr)_minmax(0,1fr)]">
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Source Field</div>
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Frontify Target</div>
        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Suggestion Note</div>
      </div>

      {/* Mapping rows */}
      {visibleMappingRows.map(({ mapping: m, index }) => (
        <MappingRow
          key={m.source_field}
          mapping={m}
          frontifyFields={frontifyFields}
          usedTargets={usedTargets}
          onChange={(updated) => handleChange(index, updated)}
          suggestLoading={suggestLoading}
        />
      ))}

      {showExcluded && excludedMappingRows.length > 0 && (
        <div className="mt-8">
          <div className="mb-3">
            <h3 className="text-sm font-semibold text-slate-800">Excluded source metadata</h3>
            <p className="text-xs text-slate-500 mt-1">
              These decisions were made by local rules, not AI. Change any field below if it has a defined Frontify use.
            </p>
          </div>
          {excludedMappingRows.map(({ mapping: m, index }) => (
            <MappingRow
              key={m.source_field}
              mapping={m}
              frontifyFields={frontifyFields}
              usedTargets={usedTargets}
              onChange={(updated) => handleChange(index, updated)}
              suggestLoading={false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
